/**
 * dealCounter.ts — the Counter, against POST /api/offers.
 *
 * Sits beside dealActions.ts rather than inside it: accept/decline/withdraw are
 * three bodiless PATCHes on an offer that already exists, and this composes a
 * whole new one. Same posture in every other respect — capture on the document,
 * installed after the write guard, never stopping propagation, the selectors
 * stay in GUARDED_WRITES, the server's own sentence is quoted, and anything it
 * cannot resolve is refused OUT LOUD.
 *
 * 🔑 A COUNTER IS NOT A DELTA. POST /api/offers creates a WHOLE new offer and
 * supersedes the parent, so whatever is not sent is not in it. The cash sheet
 * only lets the visitor change a number, which means the items have to be
 * re-stated from the row — otherwise "same swap, different price" would quietly
 * become "cash only, no items".
 *
 * ── THE THREE THINGS THAT MAKE THIS HARDER THAN IT LOOKS ──────────────────────
 *
 * 🪤 1. TWO SEND CONTROLS, ONE SHEET. renderCounterSheet has two branches on a
 * gate the visitor cannot see: if the counterpart's item resolves to a row in
 * the loaded LISTINGS page, it renders his full composer, whose send is
 * `[data-send-offer]`; otherwise it renders the cash-balance sheet, whose send
 * is `#counter-send`. The same thread flips between them purely on whether that
 * listing is in the fetched page. Wiring only one is wiring half the sheet.
 * `[data-send-offer]` is ALSO the plain "Make offer" button on a listing page —
 * that half is out of scope and stays dead, which is why every path here starts
 * from `__gbzCounterFor()` and returns silently when there is no counter.
 *
 * 🔴 2. DIRECTION. `offeredItems` and `cashAmount` are the CANONICAL BUYER's
 * side whoever clicks send — the server pins fromUserId to the parent's buyer
 * and only flips proposedByUserId. Blake's sheet always edits MY side. So a
 * seller's counter has to land in requestedItems/requestedCashAmount, or it
 * tells the server the buyer is handing over the seller's own listing. The
 * viewer's side is read from `__gbzThreadIsBuyer`, stashed by the mapper, and a
 * row that cannot answer is refused rather than guessed at.
 *
 * 🔴 3. THE ID SPACE, WHICH FAILS SILENTLY. A collection row carries `id` (a
 * user_collections key) and `lid` (the real listings.id). The offers route wants
 * `lid` — and given anything else it does NOT error: it nulls the unresolvable
 * link and inserts the row anyway, so the offer saves, both cards render from
 * the name/image/value snapshot, and only the LINK is gone. That link is what
 * locks the item at accept time; without it the item stays live and purchasable
 * after being traded away. This module never sees `id` at all: both
 * `__gbzComposerCounter` and `__gbzThreadSides` translate inside the bridge,
 * where MY_COLLECTION is in scope, and hand back the server's shape.
 *
 * ── WHAT THIS DELIBERATELY DOES NOT DO ───────────────────────────────────────
 *
 * · **The Exchange (`#ex-send`) is not wired.** Exchange-composed offers can
 *   carry a REQUESTED side, and the deal-threads wire sends only the offered one
 *   (offeredItems.ts filters `side = 'offered'`), so the sheet is not a faithful
 *   view of them. Those now route to the Exchange instead, because mapDealThread
 *   emits `viaExchange` — Blake's own rule, which had never fired because the
 *   mapper did not emit the field.
 * · **A SELLER cannot rebuild their side in the composer.** His copy says
 *   "Rebuild your side" while the API models the seller's extras as "additional
 *   items requested from the seller, BEYOND the primary listing". Those are
 *   different deals and nothing on screen says which one the visitor meant, so
 *   this refuses out loud instead of picking one. A seller countering on PRICE
 *   works — cash is unambiguous.
 */

import { installDealThread, refreshDeals } from "./floorTransport";
import { SIGNIN_MESSAGE, UNRESOLVED_OFFER_MESSAGE } from "./dealActions";

/** The cash-balance sheet's send. */
export const COUNTER_SEND_SELECTOR = "#counter-send";
/** The composer's send — ALSO the plain make-offer button, hence the counterFor gate. */
export const SEND_OFFER_SELECTOR = "[data-send-offer]";
export const COUNTER_CASH_INPUT = "#counter-cash";
export const COUNTER_NOTE_INPUT = "#counter-note";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't send that counter — please try again";
/** Blake's own words for the same refusal (app.js: sendComposerCounter). */
export const NO_ITEMS_MESSAGE = "Pick at least one item — or switch to Cash";
/** The seller-composer refusal — see the header. */
export const SELLER_COMPOSER_MESSAGE =
  "Countering with different items isn't available here yet — change the cash instead, or reply in the thread";
export const COUNTERED_MESSAGE = "Counter sent";

type CounterWindow = Window & {
  toast?: (message: string) => void;
  __gbzCounterFor?: () => string | null;
  __gbzThreadOfferId?: (threadId: string) => string | null;
  __gbzThreadIsBuyer?: (threadId: string) => boolean | null;
  __gbzThreadSides?: (threadId: string) => { mine: WireSide; theirs: WireSide } | null;
  __gbzComposerCounter?: () => { tab: string; cash: number; items: WireItem[] } | null;
  __gbzCloseCounterSheet?: () => boolean;
  __gbzClearThreadOfferId?: (threadId: string) => boolean;
};

/** The offers route's item shape. `value` absent when the row does not know it. */
export interface WireItem {
  listingId: string | null;
  name: string;
  image: string | null;
  value?: number;
}

interface WireSide {
  items: WireItem[];
  cash: number;
}

export interface CounterPayload {
  listingId: string;
  parentOfferId: string;
  offerType: "cash" | "item" | "hybrid";
  cashAmount?: number;
  requestedCashAmount?: number;
  offeredItems?: WireItem[];
  requestedItems?: WireItem[];
  message?: string;
}

/**
 * The offer type this payload implies.
 *
 * 🪤 DERIVED FROM THE PAYLOAD, NEVER FROM THE COMPOSER TAB. The server takes
 * offerType verbatim and then cross-checks it three ways, and two of those
 * refuse a body the tab name would have produced: `cashAmount > 0` with type
 * "item" is a 400 ("An item offer can't carry buyer cash — send it as a hybrid
 * offer"), and `requestedCashAmount > 0` on type "cash" is another. Reading the
 * tab would send "item" for a hybrid tab whose cash leg survived, which is the
 * enum-mismatch shape that 400'd every publish but one earlier this month.
 *
 * 🪤 AND NEVER "coin_flip". It is a real member of the enum with no branch in
 * the listing's accept-type gate, so it is a guaranteed 400 on any listing
 * created by the modern flow.
 */
export function offerTypeFor(offeredItems: WireItem[], buyerCash: number): "cash" | "item" | "hybrid" {
  if (offeredItems.length === 0) return "cash";
  return buyerCash > 0 ? "hybrid" : "item";
}

/**
 * Which side the sheet's single cash number belongs to.
 *
 * Blake's own rule, read off his handler rather than reinvented: the money lands
 * on the COUNTERPART's side unless this is an item swap where my item is worth
 * less (`gap < 0`), in which case it lands on mine. Then one more hop, which is
 * the part his fixture never had to think about — the counterpart is the seller
 * when I am the buyer, and the buyer when I am the seller.
 */
export function cashIsBuyerSide(input: {
  iAmBuyer: boolean;
  theirItemCount: number;
  myItemValue: number;
  theirItemValue: number;
}): boolean {
  const cashOffer = input.theirItemCount === 0;
  const gap = input.myItemValue - input.theirItemValue;
  const onCounterpartSide = cashOffer || gap >= 0;
  return onCounterpartSide ? !input.iAmBuyer : input.iAmBuyer;
}

/**
 * The whole payload, as a pure function, so the direction rules can be measured
 * without a DOM.
 *
 * `requestedItems` carries the seller side MINUS the primary listing: the offers
 * route models that listing as the anchor (`listingId`) and the requested array
 * as extras beyond it, so including it there would ask for the same item twice.
 */
export function buildCounterPayload(input: {
  listingId: string;
  parentOfferId: string;
  iAmBuyer: boolean;
  /** The deal as the row currently states it. */
  sides: { mine: WireSide; theirs: WireSide };
  /** Set only by the composer branch: my side, rebuilt. */
  rebuiltMine?: { items: WireItem[]; cash: number };
  /** Set only by the cash branch. */
  cash?: number;
  message?: string;
}): CounterPayload {
  const buyerSide = input.iAmBuyer ? input.sides.mine : input.sides.theirs;
  const sellerSide = input.iAmBuyer ? input.sides.theirs : input.sides.mine;

  let offeredItems = buyerSide.items;
  let buyerCash = buyerSide.cash;
  let sellerItems = sellerSide.items;
  let sellerCash = sellerSide.cash;

  if (input.rebuiltMine) {
    // The composer edits MY side only; the counterpart's stays as stated.
    if (input.iAmBuyer) {
      offeredItems = input.rebuiltMine.items;
      buyerCash = input.rebuiltMine.cash;
    } else {
      sellerItems = input.rebuiltMine.items;
      sellerCash = input.rebuiltMine.cash;
    }
  }

  if (typeof input.cash === "number") {
    const toBuyer = cashIsBuyerSide({
      iAmBuyer: input.iAmBuyer,
      theirItemCount: input.sides.theirs.items.length,
      myItemValue: input.sides.mine.items[0]?.value ?? 0,
      theirItemValue: input.sides.theirs.items[0]?.value ?? 0,
    });
    // One number moves; the other side's cash leg goes to zero. The server
    // refuses cash on both sides outright ("Cash can only ride one side of a
    // trade"), so this is the shape it requires rather than a simplification.
    buyerCash = toBuyer ? input.cash : 0;
    sellerCash = toBuyer ? 0 : input.cash;
  }

  const requestedItems = sellerItems.filter((it) => it.listingId !== input.listingId);
  const payload: CounterPayload = {
    listingId: input.listingId,
    parentOfferId: input.parentOfferId,
    offerType: offerTypeFor(offeredItems, buyerCash),
  };
  if (offeredItems.length) payload.offeredItems = offeredItems;
  if (requestedItems.length) payload.requestedItems = requestedItems;
  /* 🔴 `> 0`, AND IT IS THE ONLY THING KEEPING A NEGATIVE OFF THE WIRE. zod
     types cash_amount from PgReal, so its generated floor is gte(-8388608), and
     all three of the route's coherence gates test `> 0` as well — a negative
     amount therefore passes every server check and lands in the row, where it
     is real money pointed the wrong way. Omitting the field is also right for a
     plain zero: a cash leg of nothing is a leg that is not there. */
  if (buyerCash > 0) payload.cashAmount = buyerCash;
  if (sellerCash > 0) payload.requestedCashAmount = sellerCash;
  if (input.message) payload.message = input.message;
  return payload;
}

export function installDealCounter(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as CounterWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* In flight, per thread. His handler was synchronous and needed no guard; with
     a real await behind the button a double-tap is two counters, and the second
     supersedes the first — so the visitor's own offer would be retired by their
     own second tap. */
  const busy = new Set<string>();

  function send(payload: CounterPayload, threadId: string): Promise<void> {
    return (async () => {
      const res = await authFetch("/api/offers", {
        method: "POST",
        // authFetch does not set it, and the route parses JSON.
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        notify(SIGNIN_MESSAGE);
        return;
      }
      if (!res.ok) {
        /* The server's own words when it gave any. Every one of these is a
           sentence the visitor needs to read rather than a flattened failure:
           "This counter has the exact same terms as the offer it's countering",
           "Cash can only ride one side of a trade", "This listing does not
           accept that offer type", and the per-item "…is a cash-only item and
           can't be part of a trade."
           🪤 A zod rejection puts the first message on `error` and the rest on
           `errors[]`, and a 500 is an express HTML page with no JSON at all —
           so the parse must never be allowed to throw inside a capture listener. */
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          errors?: Array<{ message?: string }>;
        };
        notify(body.errors?.[0]?.message ?? body.error ?? GENERIC_FAILURE_MESSAGE);
        return;
      }

      /* Close the sheet FIRST. His own handler clears counterFor and the guard
         stops it, so without this the modal sits open over a row that already
         repainted underneath it — a counter that looks like it did not happen. */
      (win as CounterWindow).__gbzCloseCounterSheet?.();

      /* Then the truth, from the server. 201 returns the new offer row but not
         its items and not the parent's new `superseded` status, so there is
         nothing repaintable in it — and the words this surface renders come from
         the state machine anyway. Both re-reads are best-effort: the counter has
         already landed, so a failed GET costs a repaint, not the offer. */
      await refreshDeals(win, authFetch, myUserId).catch(() => null);
      /* 🔴 THE DETAIL RE-READ IS WHAT RE-POINTS THE ROW AT THE NEW HEAD OFFER,
         and this is the one action that mints one. If it does not land, the row
         still holds the offer we just superseded — and a second counter would
         post parentOfferId at it, which the server creates anyway rather than
         refusing. Forget it instead: the next action then resolves nothing and
         refuses out loud, which is the rule this module already lives by. */
      const reread = await installDealThread(win, authFetch, threadId, myUserId).catch(() => false);
      if (!reread) (win as CounterWindow).__gbzClearThreadOfferId?.(threadId);

      notify(COUNTERED_MESSAGE);
    })();
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    const isCashSend = !!target.closest(COUNTER_SEND_SELECTOR);
    const isComposerSend = !isCashSend && !!target.closest(SEND_OFFER_SELECTOR);
    if (!isCashSend && !isComposerSend) return;

    const w = win as CounterWindow;
    const threadId = w.__gbzCounterFor?.() ?? null;
    if (!threadId) {
      /* No counter in progress. For the composer's send that is the ORDINARY
         case — it is the make-offer button on a listing page, which is out of
         scope and stays guarded. Silence is correct there; a refusal would be
         this module claiming a control it does not own. */
      if (isCashSend) notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }

    const parentOfferId = w.__gbzThreadOfferId?.(threadId) ?? null;
    const iAmBuyer = w.__gbzThreadIsBuyer?.(threadId) ?? null;
    const sides = w.__gbzThreadSides?.(threadId) ?? null;
    const listingId = threadId.startsWith("d:") ? threadId.split(":")[2] ?? null : null;
    if (!parentOfferId || iAmBuyer === null || !sides || !listingId) {
      // Refuse out loud: an offer we cannot name, a side we cannot tell, or a
      // deal whose listing the key does not carry. Never a silent no-op.
      notify(UNRESOLVED_OFFER_MESSAGE);
      return;
    }

    if (busy.has(threadId)) return;

    let payload: CounterPayload;
    if (isCashSend) {
      const input = win.document.querySelector(COUNTER_CASH_INPUT) as HTMLInputElement | null;
      const noteEl = win.document.querySelector(COUNTER_NOTE_INPUT) as HTMLTextAreaElement | null;
      /* Unary coercion, matching his own read. NOT clamped here: the `> 0`
         gates below are what keep a negative off the wire, and a second
         mechanism for the same job is one a test cannot tell from decoration —
         a mutation removing a Math.max here survived, which is how this was
         found. One guard, in the place the payload is assembled. */
      const cash = Number(input?.value ?? 0) || 0;
      const message = noteEl?.value.trim() || undefined;
      payload = buildCounterPayload({ listingId, parentOfferId, iAmBuyer, sides, cash, message });
    } else {
      if (!iAmBuyer) {
        // See the header: his copy and the API's model describe different deals.
        notify(SELLER_COMPOSER_MESSAGE);
        return;
      }
      const composer = w.__gbzComposerCounter?.() ?? null;
      if (!composer) {
        notify(UNRESOLVED_OFFER_MESSAGE);
        return;
      }
      const items = composer.tab === "cash" ? [] : composer.items;
      if (composer.tab !== "cash" && items.length === 0) {
        notify(NO_ITEMS_MESSAGE);
        return;
      }
      /* His own number, not ours: `composerState.cash` is the number the box
         shows, the whole cash offer on the cash tab and the cash top-up on the
         hybrid tab. The total is never stored, so nothing is subtracted here;
         recomputing the cash any other way would send a number the screen
         never showed (card 1219184293238007, and Tyler's rule on card
         1218887913215108: the request carries exactly what the box shows). The
         Exchange is a separate model with its own cash box and does not come
         through here. */
      const cash = composer.tab === "items" ? 0 : Math.max(0, composer.cash);
      payload = buildCounterPayload({
        listingId,
        parentOfferId,
        iAmBuyer,
        sides,
        rebuiltMine: { items, cash },
      });
    }

    busy.add(threadId);
    void send(payload, threadId)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(threadId));
  };

  /* Capture on the document, after the guard, and deliberately not stopping
     propagation — the guard kills his fabricator and stays the fail-safe. Both
     selectors stay in GUARDED_WRITES for the reason written there. */
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
